<template>
    <div class="flex flex-wrap items-center gap-2">
        <vx-flyout v-model:open="open" closeOnSelect :width="340" @open="loadMenu">
            <template v-slot:trigger="{ open: flyoutOpen }">
                <vx-button
                    data-test="views-trigger"
                    type="button"
                    color="muted-dark"
                    class="relative"
                    :aria-expanded="flyoutOpen"
                    :aria-label="activeViewUnsaved ? `${activeLabel}, unsaved changes` : undefined"
                >
                    <font-awesome-icon widthAuto :icon="faBookmark" />
                    <span class="max-w-[220px] truncate">{{ activeLabel }}</span>
                    <font-awesome-icon widthAuto :icon="faAngleDown" class="text-[9px]" />
                    <span
                        v-if="activeViewUnsaved"
                        data-test="unsaved-dot"
                        class="absolute -end-0.5 -top-0.5 size-2.5 rounded-full bg-sky-300 ring-2 ring-white"
                    ></span>
                </vx-button>
            </template>

            <template v-slot:header>
                <div class="flex items-center justify-between gap-2">
                    Saved views
                    <vx-badge v-if="activeViewUnsaved" data-test="unsaved-changes" color="amber" size="xs">
                        <font-awesome-icon widthAuto :icon="faPen" />
                        Unsaved changes
                    </vx-badge>
                </div>
            </template>
            <template v-slot:subheader>
                A view holds the filters, the search, the sort, the grouping and the columns.
            </template>

            <vx-flyout-item data-test="all-groups" :active="props.activeView === null" @click="pickView(null)">
                <template v-slot:icon><font-awesome-icon widthAuto :icon="faTableList" /></template>
                All groups
                <template v-if="allGroupsCount !== null" v-slot:meta>{{ formatNumber(allGroupsCount) }}</template>
            </vx-flyout-item>

            <div v-if="viewsFailed" data-test="views-failed" class="px-2.5 py-1.5 text-xs text-slate-500">
                Saved views could not be loaded.
            </div>
            <template v-else>
                <div v-for="section in sections" :key="section.key" :data-test="`${section.key}-views`">
                    <span class="block px-2.5 pb-1 pt-2.5 text-xs font-extrabold text-slate-500">{{
                        section.label
                    }}</span>
                    <div v-if="!section.views.length" data-test="no-views" class="px-2.5 py-1.5 text-xs text-slate-500">
                        No views yet.
                    </div>
                    <div v-for="view in section.views" :key="view.id" class="flex items-center gap-1">
                        <vx-flyout-item
                            :data-test="`view-${view.id}`"
                            :active="props.activeView?.id === view.id"
                            @click="pickView(view)"
                        >
                            <template v-slot:icon><font-awesome-icon widthAuto :icon="faBookmark" /></template>
                            {{ view.name }}
                            <template v-slot:meta>
                                <span :data-test="`view-${view.id}-user`">{{ userLabel(view) }}</span>
                            </template>
                        </vx-flyout-item>
                        <vx-dropdown placement="right" teleport @click.stop>
                            <vx-button
                                :data-test="`view-${view.id}-actions`"
                                type="button"
                                color="transparent"
                                size="sm"
                                :aria-label="`Manage ${view.name}`"
                            >
                                <font-awesome-icon widthAuto :icon="faEllipsis" />
                            </vx-button>
                            <template v-slot:items>
                                <vx-dropdown-item
                                    data-test="rename-view"
                                    :disabled="processing"
                                    @click="openModal(view)"
                                >
                                    Rename
                                    <font-awesome-icon widthAuto :icon="faPen" />
                                </vx-dropdown-item>
                                <vx-dropdown-item
                                    data-test="update-view-to-current"
                                    :disabled="processing"
                                    @click="updateViewState(view)"
                                >
                                    Update to current
                                    <font-awesome-icon widthAuto :icon="faFloppyDisk" />
                                </vx-dropdown-item>
                                <vx-dropdown-item
                                    data-test="duplicate-view"
                                    :disabled="processing"
                                    @click="duplicateView(view)"
                                >
                                    Duplicate
                                    <font-awesome-icon widthAuto :icon="faCopy" />
                                </vx-dropdown-item>
                                <vx-dropdown-item
                                    v-if="!view.share"
                                    data-test="move-view"
                                    :disabled="processing"
                                    @click="moveView(view, true)"
                                >
                                    Move to Everyone
                                    <font-awesome-icon widthAuto :icon="faUsers" />
                                </vx-dropdown-item>
                                <vx-dropdown-item
                                    v-else-if="view.is_owner"
                                    data-test="move-view"
                                    :disabled="processing"
                                    @click="moveView(view, false)"
                                >
                                    Move to Just me
                                    <font-awesome-icon widthAuto :icon="faUser" />
                                </vx-dropdown-item>
                                <vx-dropdown-item
                                    data-test="delete-view"
                                    :disabled="processing"
                                    @click="confirmDelete(view)"
                                >
                                    Delete
                                    <font-awesome-icon widthAuto :icon="faTrash" />
                                </vx-dropdown-item>
                            </template>
                        </vx-dropdown>
                    </div>
                </div>
            </template>

            <template v-slot:footer>
                <div class="w-full grow">
                    <vx-button
                        data-test="save-as-new-view"
                        type="button"
                        color="muted-dark"
                        size="sm"
                        @click="openModal(null)"
                    >
                        <font-awesome-icon widthAuto :icon="faPlus" />
                        Save as new view
                    </vx-button>
                </div>
                <vx-button
                    data-test="update-view"
                    type="button"
                    color="muted-dark"
                    size="sm"
                    :disabled="!activeViewUnsaved || processing"
                    @click="props.activeView !== null && updateViewState(props.activeView)"
                >
                    <font-awesome-icon widthAuto :icon="faFloppyDisk" />
                    Update view
                </vx-button>
                <vx-button data-test="copy-view-link" type="button" color="muted-dark" size="sm" @click="copyLink">
                    <font-awesome-icon widthAuto :icon="faLink" />
                    Copy link
                </vx-button>
            </template>
        </vx-flyout>
        <vx-button
            v-if="props.unsaved"
            data-test="save-current-view"
            type="button"
            color="primary"
            @click="openModal(null)"
        >
            {{ props.activeView === null ? 'Save view' : 'Save as new view' }}
            <font-awesome-icon widthAuto :icon="faFloppyDisk" />
        </vx-button>
    </div>

    <kiosk-team-group-view-modal
        v-model:visible="modalVisible"
        :teamId="props.teamId"
        :view="renaming"
        :viewState="props.viewState"
        @created="emit('saved', rememberView($event))"
        @renamed="emit('updated', rememberView($event))"
        @unavailable="forgetUnavailableView"
    />
</template>

<script lang="ts" setup>
import {
    faAngleDown,
    faBookmark,
    faCopy,
    faEllipsis,
    faFloppyDisk,
    faLink,
    faPen,
    faPlus,
    faTableList,
    faTrash,
    faUser,
    faUsers,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxFlyout,
    VxFlyoutItem,
    VxSwal,
} from '@voxie/frontend-components';
import { isCancel } from 'axios';
import { computed, onMounted, ref } from 'vue';
import { useCopyText } from '~/composables/useCopyText';
import { useToasted } from '~/composables/useToasted';
import { adminTeamGroupsClient, adminTeamGroupViewsClient } from '~/services';
import type { KioskTeamGroupView, KioskTeamGroupViewState } from '~/services/adminTeamGroupViewsClient.types';
import { formatNumber } from '~/utils/helpers';
import { catchRequestError, throwSwalRequestError } from '~/utils/request';
import { disambiguateName } from '~/utils/string';
import KioskTeamGroupViewModal from './KioskTeamGroupViewModal.vue';
import {
    KIOSK_TEAM_GROUP_VIEW_MAX_NAME_LENGTH,
    KIOSK_TEAM_GROUP_VIEW_UNAVAILABLE_MESSAGE,
    isSameViewState,
    isViewNotFound,
    userLabel,
    viewHref,
    withSavedView,
} from './kioskTeamGroupsTableHelpers';

const props = defineProps<{
    teamId: number;
    activeView: KioskTeamGroupView | null;
    viewState: KioskTeamGroupViewState;
    unsaved: boolean;
}>();

const emit = defineEmits<{
    select: [view: KioskTeamGroupView | null];
    saved: [view: KioskTeamGroupView];
    updated: [view: KioskTeamGroupView];
    unavailable: [viewId: number];
    deleted: [viewId: number];
}>();

const sectionsByShare = [
    { key: 'team', share: true, label: 'Everyone' },
    { key: 'private', share: false, label: 'Just me' },
] as const;

const allGroupsCountCancelKey = Symbol('kiosk-team-group-views-count');

const toasted = useToasted();
const { copyText } = useCopyText();

const open = ref(false);
const views = ref<KioskTeamGroupView[]>([]);
const viewsFailed = ref(false);
const forgottenViewIds = new Set<number>();
let selectionCount = 0;
const allGroupsCount = ref<number | null>(null);
const modalVisible = ref(false);
const renaming = ref<KioskTeamGroupView | null>(null);
const processing = ref(false);

const activeLabel = computed(() => props.activeView?.name ?? 'All groups');

const activeViewUnsaved = computed(() => props.activeView !== null && props.unsaved);

const viewsSharedAs = (share: boolean) => views.value.filter((view) => view.share === share);

const sections = computed(() =>
    sectionsByShare.map((section) => ({ ...section, views: viewsSharedAs(section.share) }))
);

const loadViews = async () => {
    viewsFailed.value = false;

    try {
        const { data } = await adminTeamGroupViewsClient.getViews(props.teamId);

        // The list can predate a view saved or deleted while it loaded.
        views.value = views.value.reduce(
            withSavedView,
            data.data.filter((view) => !forgottenViewIds.has(view.id))
        );
    } catch {
        viewsFailed.value = true;
    }
};

const loadAllGroupsCount = async () => {
    try {
        const { data } = await adminTeamGroupsClient.getGroupsCount(
            props.teamId,
            {},
            { cancelKey: allGroupsCountCancelKey }
        );

        allGroupsCount.value = data.count;
    } catch (error) {
        if (!isCancel(error)) {
            allGroupsCount.value = null;
            toasted.error(catchRequestError(error));
        }
    }
};

const loadMenu = () => {
    loadAllGroupsCount();

    if (viewsFailed.value) {
        loadViews();
    }
};

const rememberView = (view: KioskTeamGroupView) => {
    views.value = withSavedView(views.value, view);

    return view;
};

const forgetView = (view: KioskTeamGroupView) => {
    forgottenViewIds.add(view.id);
    views.value = views.value.filter((candidate) => candidate.id !== view.id);
};

const forgetUnavailableView = (view: KioskTeamGroupView) => {
    forgetView(view);
    emit('unavailable', view.id);
};

const reportError = (view: KioskTeamGroupView, error: unknown) => {
    if (isViewNotFound(error)) {
        forgetUnavailableView(view);
        toasted.error(KIOSK_TEAM_GROUP_VIEW_UNAVAILABLE_MESSAGE);
        return;
    }

    toasted.error(catchRequestError(error));
};

const pickView = async (view: KioskTeamGroupView | null) => {
    if (!props.unsaved) {
        selectionCount++;
        emit('select', view);
        return;
    }

    open.value = false;

    const { isConfirmed } = await VxSwal.fire({
        title: 'Discard Changes',
        text: `The changes you made to ${activeLabel.value} are not saved and will be lost.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Discard',
    });

    const current = view === null ? null : views.value.find((candidate) => candidate.id === view.id);

    if (isConfirmed && current !== undefined) {
        selectionCount++;
        emit('select', current);
    }
};

const openModal = (view: KioskTeamGroupView | null) => {
    open.value = false;
    renaming.value = view;
    modalVisible.value = true;
};

const updateViewState = async (view: KioskTeamGroupView) => {
    const activeViewId = props.activeView?.id;
    const previousSelectionCount = selectionCount;
    processing.value = true;

    try {
        const { data } = await adminTeamGroupViewsClient.updateView(props.teamId, view.id, { state: props.viewState });
        const updated = rememberView(data);

        if (
            selectionCount === previousSelectionCount &&
            props.activeView?.id === activeViewId &&
            isSameViewState(props.viewState, updated.state)
        ) {
            emit('saved', updated);
        } else {
            emit('updated', updated);
        }

        toasted.success('View updated.');
    } catch (error) {
        reportError(view, error);
    } finally {
        processing.value = false;
    }
};

const moveView = async (view: KioskTeamGroupView, share: boolean) => {
    processing.value = true;

    try {
        const { data } = await adminTeamGroupViewsClient.updateView(props.teamId, view.id, { share });

        emit('updated', rememberView(data));
        toasted.success(`Moved to ${share ? 'Everyone' : 'Just me'}.`);
    } catch (error) {
        reportError(view, error);
    } finally {
        processing.value = false;
    }
};

const duplicateView = async (view: KioskTeamGroupView) => {
    const taken = new Set(viewsSharedAs(view.share).map((candidate) => candidate.name.toLowerCase()));
    processing.value = true;

    try {
        const { data } = await adminTeamGroupViewsClient.duplicateView(
            props.teamId,
            view.id,
            disambiguateName(view.name, (name) => taken.has(name.toLowerCase()), KIOSK_TEAM_GROUP_VIEW_MAX_NAME_LENGTH)
        );

        rememberView(data);
        toasted.success('View duplicated.');
    } catch (error) {
        reportError(view, error);
    } finally {
        processing.value = false;
    }
};

const deleteView = async (view: KioskTeamGroupView) => {
    try {
        await adminTeamGroupViewsClient.deleteView(props.teamId, view.id);
    } catch (error) {
        if (!isViewNotFound(error)) {
            throwSwalRequestError(error);
        }
    }

    forgetView(view);
    emit('deleted', view.id);
};

const confirmDelete = (view: KioskTeamGroupView) => {
    open.value = false;

    VxSwal.confirmation('Delete View', 'Are you sure? There is no undo.', 'danger', () => deleteView(view));
};

const copyLink = () => {
    const href = new URL(viewHref(props.teamId, props.activeView?.id ?? null), window.location.origin).toString();
    const unsavedNote = props.unsaved ? ` It opens ${activeLabel.value} without your unsaved changes.` : '';
    const privateNote = props.activeView?.share === false ? ' Only you can open it.' : '';

    copyText(href, `Link copied to clipboard.${unsavedNote}${privateNote}`);
};

onMounted(loadViews);
</script>
